import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react"
import { useApiData } from "hooks/useApiData"
import useToasts from "hooks/useToasts"
import { sendApiData } from "lib/api"
import { downloadFileFromUrl } from "lib/helpers"
import { Export, ExportEntity, ExportType, NewExport } from "models/Export"
import { PaginatedResponse } from "models/PaginatedResponse"

interface PendingTask {
  id: number
  createdAt: number
  type: ExportType
  body: unknown
  entities: ExportEntity[]
}

const taskEndpoints: Record<ExportType, string> = {
  csv: `orders/create-export-task/csv/`,
  pdf: `orders/create-export-task/pdf/merged/new`,
  zip: `orders/create-export-task/zip/`,
  "pdf/packing-slips": `orders/create-export-task/pdf/packing-slips/`,
  "pdf/shipping-labels": `orders/create-export-task/pdf/shipping-labels/`
} as const

const notificationLabels: Record<ExportType, string> = {
  csv: `CSV`,
  pdf: `PDF`,
  zip: `ZIP`,
  "pdf/packing-slips": `Packing slips`,
  "pdf/shipping-labels": `Shipping labels`
} as const

export const ExportsContext = createContext<
  | {
      pendingTasks: PendingTask[]
      createExport: (
        exportType: ExportType,
        body: unknown,
        entities: ExportEntity[]
      ) => Promise<NewExport | null>
      getTasksForEntity: (
        type: ExportEntity[`type`],
        id: ExportEntity[`id`]
      ) => PendingTask[]
    }
  | undefined
>(undefined)

export const ExportsProvider = ({ children }: { children: ReactNode }) => {
  const dispatchToast = useToasts(state => state.dispatchToast)
  const dismissToast = useToasts(state => state.dismissToast)
  const [shouldPoll, setShouldPoll] = useState(false)
  const [pendingTasks, setPendingTasks] = useState<PendingTask[]>([])
  const { data } = useApiData<PaginatedResponse<Export>>(`brands/exports/`, {
    refreshInterval: shouldPoll ? 3000 : 0,
    dedupingInterval: 3000
  })

  const createExport = useCallback(
    async (exportType: ExportType, body: unknown, entities: ExportEntity[]) => {
      // Optimistically add a pending task
      const tempId = Date.now()
      setPendingTasks(current =>
        current.concat({
          id: tempId,
          createdAt: Date.now(),
          type: exportType,
          body,
          entities
        })
      )

      try {
        const response = await sendApiData<NewExport>(
          `brands/${taskEndpoints[exportType]}`,
          {
            method: `POST`,
            json: body
          }
        )

        setPendingTasks(current =>
          current.map(task =>
            task.id === tempId ? { ...task, id: response.taskId } : task
          )
        )

        setShouldPoll(true)
        return response
      } catch (error) {
        setPendingTasks(current => current.filter(task => task.id !== tempId))
        throw error
      }
    },
    []
  )

  const getTasksForEntity = useCallback(
    (type: ExportEntity[`type`], id: ExportEntity[`id`]) =>
      pendingTasks.filter(task =>
        task.entities.some(entity => entity.type === type && entity.id === id)
      ),
    [pendingTasks]
  )

  useEffect(() => {
    if (!data?.results) return

    const newlyCompletedTasks = data.results
      .map(task => ({
        ...task,
        pendingTask: pendingTasks.find(pending => pending.id === task.id)
      }))
      .filter(
        task => task.state === `Completed` && task.pendingTask !== undefined
      )

    if (newlyCompletedTasks.length) {
      newlyCompletedTasks.forEach(task => {
        if (Date.now() - task.pendingTask!.createdAt > 60000) {
          dispatchToast(
            {
              type: `success`,
              // eslint-disable-next-line react/no-unstable-nested-components
              message: id => (
                <span>
                  Your {notificationLabels[task.pendingTask!.type]} is ready,
                  {` `}
                  <a
                    href={task.downloadUrl}
                    className="underline"
                    download
                    onClick={() => dismissToast(id)}
                  >
                    click here
                  </a>
                  {` `}
                  to download.
                </span>
              )
            },
            false
          )
        } else {
          downloadFileFromUrl(task.downloadUrl)
        }
      })

      const newlyCompletedTasksIds = newlyCompletedTasks.map(task => task.id)
      const updatedPendingTasks = pendingTasks.filter(
        task => !newlyCompletedTasksIds.includes(task.id)
      )
      setPendingTasks(updatedPendingTasks)
      setShouldPoll(updatedPendingTasks.length > 0)
    }
  }, [data?.results, dismissToast, dispatchToast, pendingTasks])

  const value = useMemo(
    () => ({
      pendingTasks,
      createExport,
      getTasksForEntity
    }),
    [createExport, pendingTasks, getTasksForEntity]
  )

  return (
    <ExportsContext.Provider value={value}>{children}</ExportsContext.Provider>
  )
}

export const useExportsContext = () => {
  const context = useContext(ExportsContext)

  if (!context) {
    if (!context) {
      throw new Error(`useExportsContext must be used within a ExportsProvider`)
    }
  }

  return context
}
